import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMin, fmtTime } from "@/lib/format";
import { StatusBadge, SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Clock } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { field: "unloading_start", label: "Start Unloading", needs: [] },
  { field: "unloading_end", label: "Complete Unloading", needs: ["unloading_start"] },
  { field: "qc_start", label: "Start QC", needs: ["unloading_end"] },
  { field: "qc_end", label: "Complete QC", needs: ["qc_start"] },
  { field: "grn_time", label: "GRN Done", needs: ["qc_end"] },
  { field: "putaway_start", label: "Start Put-away", needs: ["grn_time"] },
  { field: "putaway_end", label: "Complete Put-away", needs: ["putaway_start"] },
];

export default function Inward() {
  const { user } = useAuth();
  const [recs, setRecs] = useState([]);
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(null);
  const [qcFail, setQcFail] = useState({ qty: "", reason: "" });
  const [form, setForm] = useState({ supplier: "", po_number: "", vehicle_number: "", driver_name: "", material: "", sku: "", quantity: "", units: "", unit_type: "cartons", unit_price: "", freight: "", loading_charges: "", direct_charges: "", batch: "", warehouse: "" });
  const [warehouses, setWarehouses] = useState([]);
  const canCreate = ["admin", "warehouse_manager", "supervisor"].includes(user?.role);

  const load = useCallback(async () => {
    const res = await api.get("/inward");
    setRecs(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/warehouses").then((r) => setWarehouses(r.data.filter((w) => w.status === "active"))).catch(() => setWarehouses([]));
  }, []);

  const create = async () => {
    try {
      await api.post("/inward", { ...form, quantity: parseInt(form.quantity) || 0, units: form.units ? parseInt(form.units) : null,
        unit_price: form.unit_price ? parseFloat(form.unit_price) : null, freight: form.freight ? parseFloat(form.freight) : null,
        loading_charges: form.loading_charges ? parseFloat(form.loading_charges) : null,
        direct_charges: form.direct_charges ? parseFloat(form.direct_charges) : null, batch: form.batch || null,
        warehouse: form.warehouse || null });
      toast.success("Inward entry created — vehicle arrival time recorded");
      setOpen(false);
      setForm({ supplier: "", po_number: "", vehicle_number: "", driver_name: "", material: "", sku: "", quantity: "", units: "", unit_type: "cartons", unit_price: "", freight: "", loading_charges: "", direct_charges: "", batch: "", warehouse: "" });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const stamp = async (inwardNo, field) => {
    try {
      const res = await api.post(`/inward/${inwardNo}/timestamp`, { field });
      toast.success("Timestamp recorded");
      setManage(res.data);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const F = (key, label, half = true) => (
    <div className={half ? "" : "col-span-2"}>
      <Label>{label}</Label>
      <Input data-testid={`inward-${key}-input`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="inward-page">
      <SectionTitle
        right={
          canCreate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="new-inward-button"><Plus className="w-4 h-4 mr-1" /> New Inward</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-heading text-xl uppercase">New Inward Entry</DialogTitle><DialogDescription className="sr-only">Record an incoming vehicle and material details</DialogDescription></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  {F("supplier", "Supplier", false)}
                  {F("po_number", "PO Number")}
                  {F("vehicle_number", "Vehicle Number")}
                  {F("driver_name", "Driver Name")}
                  {F("material", "Material")}
                  {F("sku", "SKU")}
                  <div>
                    <Label>Quantity</Label>
                    <Input data-testid="inward-quantity-input" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                  </div>
                  <div>
                    <Label>Bags/Cartons</Label>
                    <Input data-testid="inward-units-input" type="number" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} />
                  </div>
                  {F("batch", "Batch / Lot")}
                  <div>
                    <Label>Warehouse</Label>
                    <Select value={form.warehouse} onValueChange={(v) => setForm({ ...form, warehouse: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="inward-warehouse-select"><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        {warehouses.map((w) => <SelectItem key={w.warehouse_id} value={w.name} data-testid={`inward-warehouse-${w.code}`}>{w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {F("unit_price", "Unit Purchase Price ₹")}
                  {F("freight", "Freight ₹")}
                  {F("loading_charges", "Loading / Unloading ₹")}
                  {F("direct_charges", "Other Direct Charges ₹")}
                  <p className="col-span-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">When a unit price is entered, stock is posted with a FIFO cost layer (landed cost = purchase + freight + charges).</p>
                </div>
                <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground mt-2" onClick={create} data-testid="inward-create-submit">Record Vehicle Arrival</Button>
              </DialogContent>
            </Dialog>
          )
        }
      >
        Inward / Goods Receiving
      </SectionTitle>

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="inward-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Inward No", "Supplier", "Vehicle", "Material / SKU", "Qty", "Arrival", "Wait", "Unload", "QC", "Put-away", "Total", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {recs.map((r) => (
              <tr key={r.inward_no} className="border-b border-border/50 hover:bg-accent/40 transition-colors duration-150" data-testid={`inward-row-${r.inward_no}`}>
                <td className="px-4 py-3 metric font-bold text-primary whitespace-nowrap">{r.inward_no}</td>
                <td className="px-4 py-3">{r.supplier}</td>
                <td className="px-4 py-3 metric">{r.vehicle_number}</td>
                <td className="px-4 py-3 text-muted-foreground">{r.material} <span className="metric text-xs">({r.sku})</span></td>
                <td className="px-4 py-3 metric">{r.quantity.toLocaleString()}</td>
                <td className="px-4 py-3 metric text-xs">{fmtTime(r.arrival_time)}</td>
                <td className={`px-4 py-3 metric ${(r.vehicle_waiting_minutes ?? 0) > 30 ? "text-signal-red font-bold" : ""}`}>{fmtMin(r.vehicle_waiting_minutes)}</td>
                <td className="px-4 py-3 metric">{fmtMin(r.unloading_minutes)}</td>
                <td className="px-4 py-3 metric">{fmtMin(r.qc_minutes)}</td>
                <td className="px-4 py-3 metric">{fmtMin(r.putaway_minutes)}</td>
                <td className="px-4 py-3 metric font-bold">{fmtMin(r.total_processing_minutes)}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  {r.status !== "completed" && (
                    <Button size="sm" variant="outline" onClick={() => setManage(r)} data-testid={`inward-manage-${r.inward_no}`}>
                      <Clock className="w-3.5 h-3.5 mr-1" /> Update
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {recs.length === 0 && <tr><td colSpan={13} className="px-4 py-8 text-center text-muted-foreground">No inward records</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!manage} onOpenChange={() => setManage(null)}>
        <DialogContent className="bg-card border-border" data-testid="inward-manage-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">{manage?.inward_no} — {manage?.vehicle_number}</DialogTitle>
            <DialogDescription className="sr-only">Update inward processing timestamps</DialogDescription>
          </DialogHeader>
          {manage && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Arrival recorded at <span className="metric">{fmtTime(manage.arrival_time)}</span> · {manage.supplier} · {manage.material} ({manage.quantity.toLocaleString()} pcs)</p>
              {STEPS.map((s) => {
                const done = !!manage[s.field];
                const ready = s.needs.every((n) => manage[n]);
                return (
                  <div key={s.field} className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                    <span className="text-sm">{s.label}</span>
                    {done ? (
                      <span className="metric text-sm text-signal-green font-bold">{fmtTime(manage[s.field])}</span>
                    ) : (
                      <Button size="sm" disabled={!ready} onClick={() => stamp(manage.inward_no, s.field)} className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-40" data-testid={`inward-stamp-${s.field}`}>
                        Stamp now
                      </Button>
                    )}
                  </div>
                );
              })}
              {manage.qc_start && !manage.putaway_end && (
                <div className="border border-signal-red/40 rounded-md p-2.5 space-y-2" data-testid="inward-qc-fail-form">
                  <p className="text-xs font-mono uppercase tracking-widest text-signal-red">QC Fail → Quarantine</p>
                  {manage.qc_failed_qty ? (
                    <p className="text-xs text-muted-foreground" data-testid="qc-fail-recorded">{manage.qc_failed_qty} pcs quarantined — {manage.qc_fail_reason}</p>
                  ) : (
                    <div className="flex gap-2 items-end flex-wrap">
                      <div><Label className="text-xs">Failed qty</Label><Input className="w-24 h-8" type="number" data-testid="qc-fail-qty" value={qcFail.qty} onChange={(e) => setQcFail({ ...qcFail, qty: e.target.value })} /></div>
                      <div className="flex-1 min-w-40"><Label className="text-xs">Reason *</Label><Input className="h-8" data-testid="qc-fail-reason" value={qcFail.reason} onChange={(e) => setQcFail({ ...qcFail, reason: e.target.value })} /></div>
                      <Button size="sm" variant="outline" className="h-8 border-signal-red/50 text-signal-red" data-testid="qc-fail-submit"
                        disabled={!qcFail.qty || !qcFail.reason}
                        onClick={async () => {
                          try {
                            await api.post(`/inward/${manage.inward_no}/qc-fail`, { failed_qty: parseInt(qcFail.qty) || 0, reason: qcFail.reason });
                            toast.success("QC failure recorded — stock quarantined");
                            setQcFail({ qty: "", reason: "" });
                            setManage(null);
                            load();
                          } catch (e) {
                            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
                          }
                        }}>Quarantine</Button>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 pt-2 text-center">
                <div className="border border-border rounded-md p-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Vehicle wait</p>
                  <p className="metric font-bold">{fmtMin(manage.vehicle_waiting_minutes)}</p>
                </div>
                <div className="border border-border rounded-md p-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Total processing</p>
                  <p className="metric font-bold">{fmtMin(manage.total_processing_minutes)}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
